/**
 * Hook utility per fetchare la stagione attiva + regolamento.
 *
 * Riutilizzato da RosePage / RegolePage / StoricoPage.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";

export interface StagioneSummary {
  id: string;
  anno: string;
  modalita: "mantra" | "classic";
  stato:
    | "preparazione"
    | "mercato_estivo"
    | "lista_provvisoria"
    | "in_corso"
    | "mercato_invernale"
    | "conclusa";
  data_inizio: string | null;
  data_fine: string | null;
  is_prima_stagione: boolean;
  giornata_serie_a_inizio: number;
  giornata_serie_a_fine: number;
}

/** Lista completa stagioni (autenticato, no admin required). */
export function useStagioni() {
  const isAuth = !!useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["stagioni-all"],
    queryFn: async () => (await api.get<StagioneSummary[]>("/admin/stagioni")).data,
    enabled: isAuth,
  });
}

/** Stagione attiva, null se nessuna.
 *
 * Considera "attiva" qualsiasi stagione non `conclusa`:
 * - in_corso (priorità)
 * - mercato_estivo / mercato_invernale / lista_provvisoria
 * - preparazione (stagione appena creata, pronta per setup)
 *
 * NON include `conclusa`. Per pagine di consultazione (rose, classifica,
 * regole, budget, tesoreria) usa invece [[useStagioneCorrente]] che
 * fallback sull'ultima conclusa.
 */
export function useStagioneAttiva() {
  const q = useStagioni();
  const stagione =
    q.data?.find((s) => s.stato === "in_corso") ??
    q.data?.find((s) =>
      ["mercato_estivo", "mercato_invernale", "lista_provvisoria"].includes(s.stato),
    ) ??
    q.data?.find((s) => s.stato === "preparazione") ??
    null;
  return { ...q, data: stagione };
}

/** Stagione "corrente" per pagine di consultazione (rose, classifica,
 * regole, budget, tesoreria, palmares).
 *
 * Logica: stagione ATTIVA se esiste, altrimenti l'ULTIMA CONCLUSA per anno
 * DESC. Cosi' chi consulta la propria rosa o la classifica continua a
 * vedere i dati anche dopo la chiusura, finche' non viene creata la
 * stagione successiva.
 *
 * Per pagine che richiedono giornate `da_giocare` (formazione, mercato,
 * asta), continua ad usare [[useStagioneAttiva]] perche' su una stagione
 * conclusa non ha senso schierare/proporre.
 */
export function useStagioneCorrente() {
  const q = useStagioni();
  const attiva =
    q.data?.find((s) => s.stato === "in_corso") ??
    q.data?.find((s) =>
      ["mercato_estivo", "mercato_invernale", "lista_provvisoria"].includes(s.stato),
    ) ??
    q.data?.find((s) => s.stato === "preparazione") ??
    null;
  const ultimaConclusa =
    q.data?.filter((s) => s.stato === "conclusa").sort((a, b) => b.anno.localeCompare(a.anno))[0] ??
    null;
  return { ...q, data: attiva ?? ultimaConclusa };
}

/** Stagioni concluse, ordinate per anno DESC. */
export function useStagioniConcluse() {
  const q = useStagioni();
  const concluse =
    q.data?.filter((s) => s.stato === "conclusa").sort((a, b) => b.anno.localeCompare(a.anno)) ??
    [];
  return { ...q, data: concluse };
}

// === Palmares (vincitori di una stagione) ===

export interface PalmaresResponse {
  stagione_id: string;
  anno: string;
  stato: StagioneSummary["stato"];
  campionato_squadra_id: string | null;
  cl_squadra_id: string | null;
  supercoppa_squadra_id: string | null;
  /** nomi scelti dalla lega (Andrea 2026-10-08) */
  campionato_nome?: string | null;
  cl_nome?: string | null;
  supercoppa_nome?: string | null;
}

/** Palmares di una stagione specifica. */
export function usePalmares(stagioneId: string | null) {
  const isAuth = !!useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["palmares", stagioneId],
    queryFn: async () => (await api.get<PalmaresResponse>(`/stagioni/${stagioneId}/palmares`)).data,
    enabled: isAuth && !!stagioneId,
  });
}

/** Palmares dell'ultima stagione CONCLUSA. null se nessuna. Usato dalla
 * dashboard per il banner vincitore e dalle medaglie sulle squadre. */
export function usePalmaresUltimaConclusa() {
  const isAuth = !!useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["palmares", "ultima-conclusa"],
    queryFn: async () =>
      (await api.get<PalmaresResponse | null>("/stagioni/ultima-conclusa/palmares")).data,
    enabled: isAuth,
  });
}

// === Regolamento ===

interface BonusMalusItem {
  evento: string;
  punteggio: string;
}

interface SogliaGolItem {
  punteggio_minimo: string;
  gol_assegnati: number;
}

interface BonusCapitanoItem {
  voto_base_minimo: string;
  bonus_capitano: string;
}

export interface RegolamentoFull {
  regolamento_id: string | null;
  stagione_id: string;
  congelato_at: string | null;
  modificatore_difesa_attivo: boolean;
  modificatore_difesa_min_difensori: number;
  /** D Factor: il voto del portiere entra nella media (Andrea 2026-09-11). */
  modificatore_difesa_includi_portiere: boolean;
  mantra_ruoli_difesa: string[];
  sv_comportamento: string;
  panchina_struttura: string;
  numero_panchinari: number;
  max_sostituzioni: number | null;
  formazione_non_schierata: string;
  formazione_non_schierata_punteggio_fisso: string | null;
  fairplay_attivo: boolean;
  voto_giocatori_rinvio_sa: string;
  spareggio_ko: string;
  spareggio_supplementari_attivo: boolean;
  spareggio_supplementari_n_panchinari: number;
  spareggio_supplementari_modalita: string;
  bonus_malus: BonusMalusItem[];
  soglie_gol: SogliaGolItem[];
  bonus_capitano: BonusCapitanoItem[];
  modificatore_difesa: {
    media_voto_minimo: string;
    bonus: string;
  }[];
  fairplay: {
    descrizione: string;
    zero_ammonizioni: boolean;
    zero_espulsioni: boolean;
    bonus: string;
  }[];
}

export function useRegolamento(stagioneId: string | null) {
  const isAuth = !!useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["regolamento", stagioneId],
    queryFn: async () => (await api.get<RegolamentoFull>(`/admin/regolamento/${stagioneId}`)).data,
    enabled: isAuth && !!stagioneId,
  });
}
